//SETTINGS: Siloed game config variables & defaults

import { Forest, DailyExercise } from '../types';

export const GAME_CONFIG = {
  //Game length
  DEFAULT_GAME_DURATION: 10, //NOTE: Denominated in days

  //Days to full tree maturity
  TREE_MATURITY_TIME: 5, //NOTE: 5 days for 6 stages
  
  //Effect of water, self & others
  SELF_WATER_EFFECT: 0.07, //Percent reduction in maturity time when watering self trees
  OTHER_WATER_EFFECT: 0.15, //Percent reduction in maturity time when watering other's trees
  
  //Daily limits and rewards
  SEED_PER_DAILY_EXERCISE: 1, //Denominated in seeds (duh)
  WATER_PER_CHATBOT_INTERACTION: 1, //Denominated in gallons
  DEFAULT_DAILY_WATER_LIMIT: undefined, //Gallons per day. Here, undefined = unlimited
  
  //Game grid dimensions (denominated in "tiles")
  GRID_WIDTH: 64,
  GRID_HEIGHT: 36,
  TILE_SIZE: 64,

  //Viewport sizes ToDelete
  //VIEWPORT_WIDTH: 16,
  //VIEWPORT_HEIGHT: 9,
  
  //Screen layout percentages
  FOREST_WIDTH_PERCENT: 92, //Percent of total screen, to leave room for borders
  FOREST_HEIGHT_PERCENT: 92,//Percent of total screen
  STATUS_BAR_HEIGHT_PERCENT: 15, //Etc.
  BORDER_PERCENT: 2,
  ACTIVE_GAME_AREA_PERCENT: 100,

  QUICK_MODE: {
    ENABLED_BY_DEFAULT: false, //Can be overridden per forest
    TIME_MULTIPLIER: 1440, //NOTE: Set to MINUTE schedule. ToDo: is this correct?
    NORMAL_TIME_MULTIPLIER: 1, //Normal speed
    MAX_QUICK_DURATION_MINUTES: 60, //Max 60 minutes for quick games
  },
  
  //Tree growth stages
  TREE_STAGES: {
    SEED: 0,        //Day 0-1
    SPROUT: 1,      //Day 1-2  
    SEEDLING: 2,    //Day 2-3
    SAPLING: 3,     //Day 3-4
    YOUNG: 4,       //Day 4-5
    MATURE: 5,      //Day 5+ (fully mature)
  } as const,
  
  //Password reqs
  PASSWORD_MIN_LENGTH: 12,
  PASSWORD_REQUIREMENTS: {
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
  },
  
  //Chat modal dimensions (percentages)
  CHAT_MODAL: {
    TEXT_WIDTH_PERCENT: 60,
    TEXT_HEIGHT_PERCENT: 85,
  },
  
  //Animation durations (milliseconds)
  ANIMATIONS: {
    FADE_DURATION: 500,
    PULSE_DURATION: 1000,
    SCENE_TRANSITION: 1000,
  },

  //Controls Fast test mode for development. This should be deleted after beta testing, probably
  FAST_MODE: {
    ENABLED: false, //Set to true to enable fast mode  ToDo:  Use flag to accomplish this
    GROWTH_MULTIPLIER: 1440, //1 day == 1 min
    GAME_DURATION_MINUTES: 10, //10 minutes for full game //ToDo: reconcile with user vars, above
    WATER_COOLDOWN_SECONDS: 2, //Faster water actions
    SEED_COOLDOWN_SECONDS: 1, //Faster seed planting
  },
} as const;


export const ASSET_PATHS = {
  BACKGROUNDS: {
    LOGIN: '/assets/images/backgrounds/login-background.jpg',
    MODAL: '/assets/images/backgrounds/modal-background.jpg',
    FOREST: '/assets/images/backgrounds/forest-background.jpg',
  },
  
  TILES: {
    PLANTABLE: '/assets/images/tiles/plantable.png',
    WALKABLE: [
      '/assets/images/tiles/walkable-1.png',
      '/assets/images/tiles/walkable-2.png',
      '/assets/images/tiles/walkable-3.png',
    ],
    BORDER: [
      '/assets/images/tiles/border-1.png',
      '/assets/images/tiles/border-2.png',
      '/assets/images/tiles/border-3.png',
    ],
  },
  
  //Tree stages
  TREES: {
    SEED: '/assets/images/trees/seed.png',         //Stage 0
    SPROUT: '/assets/images/trees/sprout.png',     //Stage 1
    SEEDLING: '/assets/images/trees/seedling.png', //Stage 2
    SAPLING: '/assets/images/trees/sapling.png',   //Stage 3
    YOUNG: '/assets/images/trees/young.png',       //Stage 4
    MATURE: '/assets/images/trees/mature.png',     //Stage 5
  },
  
  ICONS: {
    SEED: '/assets/images/icons/seed.png',
    WATER: '/assets/images/icons/water.png',
    CHAT: '/assets/images/icons/chat.png',
    EXERCISE: '/assets/images/icons/exercise.png',
    PROFILE: '/assets/images/icons/profile.png',
    ARROW_LEFT: '/assets/images/icons/arrow-left.png',
    ARROW_RIGHT: '/assets/images/icons/arrow-right.png',
    CLOSE: '/assets/images/icons/close.png',
  },
} as const;

//SETTINGS: Colors
export const COLORS = {
  PRIMARY: 'sage-500',
  SECONDARY: 'lavender-400',
  ACCENT: 'peach-400',
  BACKGROUND: 'powder-50',
  TEXT: 'powder-800',
  ERROR: 'red-500',
  SUCCESS: 'green-500',
  WARNING: 'yellow-500',
} as const;

//Fonts
export const FONTS = {
  PRIMARY: 'Inter',
  FALLBACK: ['ui-sans-serif', 'system-ui', 'sans-serif'],
} as const;

//Helper functions for game mechanics
export const getTreeGrowthTime = (maturityStage: number, fastMode = false): number => {
  const baseDays = GAME_CONFIG.TREE_MATURITY_TIME / Object.keys(GAME_CONFIG.TREE_STAGES).length;
  
  if (fastMode && GAME_CONFIG.FAST_MODE.ENABLED) {
    //Convert days to minutes in fast mode
    return (baseDays * 24 * 60) / GAME_CONFIG.FAST_MODE.GROWTH_MULTIPLIER;
  }
  
  return baseDays * 24 * 60 * 60 * 1000; //Convert to milliseconds
};

export const calculateWaterEffect = (
  currentGrowthTime: number, 
  isOwnTree: boolean
): number => {
  const reduction = isOwnTree 
    ? GAME_CONFIG.SELF_WATER_EFFECT 
    : GAME_CONFIG.OTHER_WATER_EFFECT;
  
  return currentGrowthTime * (1 - reduction);
};

//Fast mode toggle
export const toggleFastMode = (): void => {
  //@ts-ignore
  GAME_CONFIG.FAST_MODE.ENABLED = !GAME_CONFIG.FAST_MODE.ENABLED;
  //ToDo: Add in actual time vars
  console.log(`Fast mode ${GAME_CONFIG.FAST_MODE.ENABLED ? 'ENABLED' : 'DISABLED'}`);
  console.log(`Tree growth: ${GAME_CONFIG.FAST_MODE.ENABLED ? '1 day = 1 minute' : 'Normal speed'}`);
  console.log(`Game duration: ${GAME_CONFIG.FAST_MODE.ENABLED ? '10 minutes' : '10 days'}`);
};

//Console helper for testing (callable from browser console for local testing, for Muscles, etc.)
if (typeof window !== 'undefined') {
  //@ts-ignore
  window.gameDebug = {
    toggleFastMode,
    config: GAME_CONFIG,
    assets: ASSET_PATHS,
  };
}


//QUICKMODE HELPERS

export const getTimeMultiplier = (forest: Forest): number => {
  if (forest.isQuickGame && forest.timeMultiplier) {
    return forest.timeMultiplier;
  }
  return forest.isQuickGame ? GAME_CONFIG.QUICK_MODE.TIME_MULTIPLIER : GAME_CONFIG.QUICK_MODE.NORMAL_TIME_MULTIPLIER;
};

/**
 * Game time -> real time
 * @param gameDays - Number of game days
 * @param forest - Forest configuration
 * @returns Real time in milliseconds
 */

export const gameTimeToRealTime = (gameDays: number, forest: Forest): number => {
  const multiplier = getTimeMultiplier(forest);
  const baseDayMs = 24 * 60 * 60 * 1000; //24 hours in milliseconds
  
  if (forest.isQuickGame) {
    //Quick mode (default: 1 day is 1 min)
    return gameDays * 60 * 1000;
  } else {
    //Normal mode: (1 day = 24 hours)
    return gameDays * baseDayMs;
  }
};

/**
 * Real duration of a game day in ms
 */
export const getGameDayDuration = (forest: Forest): number => {
  return gameTimeToRealTime(1, forest);
};

/**
 * Tree growth by time
 */
export const calculateTreeGrowthStage = (
  plantedAt: Date, 
  forest: Forest, 
  currentTime: Date = new Date()
): number => {
  const timeElapsed = currentTime.getTime() - plantedAt.getTime();
  const dayDuration = getGameDayDuration(forest);
  const daysElapsed = timeElapsed / dayDuration;
  
  //Trees: 6 stages (0-5) over 5 game days
  const stageTime = 5 / 6; //Days per stage
  const stage = Math.min(5, Math.floor(daysElapsed / stageTime));
  
  return stage;
};

/**
 * Exercise unlock
 */
export const calculateExerciseUnlockTime = (
  exerciseDay: number, 
  forest: Forest
): Date => {
  const startTime = forest.startDate.getTime();
  const dayDuration = getGameDayDuration(forest);
  
  //Exercise unlocks at start of day
  const unlockTime = startTime + ((exerciseDay - 1) * dayDuration);
  
  return new Date(unlockTime);
};

/**
 * Water effects
 */
export const calculateWaterEffectDuration = (
  baseEffectHours: number,
  forest: Forest
): number => {
  const multiplier = getTimeMultiplier(forest);
  
  if (forest.isQuickGame) {
    //Scaled water effects
    return (baseEffectHours * 60 * 60 * 1000) / multiplier;
  } else {
    //Normal
    return baseEffectHours * 60 * 60 * 1000; //In ms
  }
};

/**
 * Display text for game duration
 */
export const getGameDurationDisplay = (forest: Forest): string => {
  if (forest.isQuickGame) {
    return `${forest.gameDuration} minutes (Quick Mode)`;
  } else {
    return `${forest.gameDuration} days`;
  }
};

/**
 * currently active forest?
 */
export const isForestCurrentlyActive = (forest: Forest, currentTime: Date = new Date()): boolean => {
  const startTime = forest.startDate.getTime();
  const endTime = forest.endDate.getTime();
  const now = currentTime.getTime();
  
  //Quick games
  if (forest.isQuickGame) {
    const quickEndTime = startTime + gameTimeToRealTime(forest.gameDuration, forest);
    return now >= startTime && now <= quickEndTime && forest.isActive;
  } else {
    //Normal
    return now >= startTime && now <= endTime && forest.isActive;
  }
};

/**
 * Current day
 */
export const getCurrentGameDay = (forest: Forest, currentTime: Date = new Date()): number => {
  const startTime = forest.startDate.getTime();
  const now = currentTime.getTime();
  
  if (now < startTime) {
    return 0;
  }
  
  const timeElapsed = now - startTime;
  const dayDuration = getGameDayDuration(forest);
  const currentDay = Math.floor(timeElapsed / dayDuration) + 1;
  
  return Math.min(currentDay, forest.gameDuration);
};

/**
 * Available exercises for current time
 */
export const getAvailableExercises = (
  exercises: DailyExercise[], 
  forest: Forest, 
  currentTime: Date = new Date()
): DailyExercise[] => {
  const currentDay = getCurrentGameDay(forest, currentTime);
  
  return exercises.filter(exercise => exercise.day <= currentDay);
};

/**
 * Calc tree maturity
 */
export const calculateTreeMaturityDate = (
  plantedAt: Date,
  waterReceived: number,
  forest: Forest
): Date => {
  const baseDayDuration = getGameDayDuration(forest);
  const baseMaturityTime = baseDayDuration * 5;
  
  //Water effects
  const waterSpeedup = waterReceived * 0.1; //10% faster per water ToDo: Reconcile with stat, above
  const adjustedMaturityTime = baseMaturityTime * (1 - Math.min(0.5, waterSpeedup)); //ToDo; Delete and test
  
  return new Date(plantedAt.getTime() + adjustedMaturityTime);
};

/**
 * Quick mode debug
 */
export const quickModeDebug = {

  logTimeConversion: (forest: Forest): void => {
    console.log('Quick Mode Time Conversion:', {
      isQuickGame: forest.isQuickGame,
      timeMultiplier: getTimeMultiplier(forest),
      oneDayInMs: getGameDayDuration(forest),
      oneDayInMinutes: getGameDayDuration(forest) / (60 * 1000),
      totalGameDurationMs: gameTimeToRealTime(forest.gameDuration, forest),
      totalGameDurationMinutes: gameTimeToRealTime(forest.gameDuration, forest) / (60 * 1000),
    });
  },

  /**
   * Time sim for testing
   */
  simulateTime: (forest: Forest, minutesFromStart: number): {
    currentGameDay: number;
    availableExercises: number;
    timeElapsed: string;
  } => {
    const simulatedTime = new Date(forest.startDate.getTime() + (minutesFromStart * 60 * 1000));
    const currentDay = getCurrentGameDay(forest, simulatedTime);
    
    return {
      currentGameDay: currentDay,
      availableExercises: currentDay,
      timeElapsed: `${minutesFromStart} minutes`,
    };
  },

  /**
   * Quick mdoe stats summary
   */
  getStatus: (forest: Forest): string => {
    if (!forest.isQuickGame) {
      return 'Normal Mode: 1 day = 24 hours';
    }
    
    const multiplier = getTimeMultiplier(forest);
    const dayMinutes = getGameDayDuration(forest) / (60 * 1000);
    const totalMinutes = gameTimeToRealTime(forest.gameDuration, forest) / (60 * 1000);
    
    return `Quick Mode: 1 day = ${dayMinutes}min (${multiplier}x speed), Total: ${totalMinutes}min`;
  }
};

//Console helpers
if (typeof window !== 'undefined') {
  //@ts-ignore
  window.quickModeDebug = quickModeDebug;
}